import React, {
  ChangeEvent,
  ChangeEventHandler,
  useEffect,
  useContext,
  useState,
  Fragment
} from 'react'
import Hr from '../Hr/Hr'
import * as ArchiveStyles from './Archive.module.scss'
import * as BreadcrumbStyles from '../organisms/breadcrumb/Breadcrumb.module.scss'
import Subnav from '../SubNav/SubNav'
import SkeletonBlocks from '../SkeletonBlocks/Skeleton'
import { Link } from 'gatsby'
import { Category, LbbCategory } from '../../types'
import Map from './Map'
// eslint-disable-next-line node/no-extraneous-import
import { gql, useQuery } from '@apollo/client'
import { StaticImage } from 'gatsby-plugin-image'
import * as BPTLStyles from '../layoutComponents/bptl/BPTL.module.scss'
import ArchiveBlock from './ArchiveBlock'
import BPTLBlock from './BPTLBlock'
import { bptlVote } from '../../api/votebptl'
import { BPTLContext, BPTLStates } from '../../lib/utils'
import BPTL_RESULTS from '../../lib/data/BPTL'
import Blocks from '../Blocks/Blocks'
import MPUBanner from '../Banners/MPUBanner/MPUBanner'
import { MULTISITE_COUNTIES } from '../../lib/data/Sites'
import classNames from 'classnames'

interface ArchiveProps {
  category: Category | LbbCategory
  parentCategory?: Category | LbbCategory | null
  isBestPlacestoLive?: boolean
  postType?: Record<string, string>
  countyLatLon?: string | null
  pagination: number
  siteId?: number | null
}

const GET_POSTS = gql`
  query ($id: Int, $skip: Float) {
    nationalPosts(categoryId: $id, first: $skip) {
      id
      title
      uri
      linkType
      primaryCategory
      primaryCategoryURI
      spotlightDesktopImage
      spotlightMobileImage
      homepagePreviewImage
    }
  }
`

const GET_BPTL = gql`
  query ($id: Int, $skip: Float) {
    bptlPosts(categoryId: $id, first: $skip) {
      id
      title
      uri
      linkType
      primaryCategory
      primaryCategoryURI
      spotlightDesktopImage
      spotlightMobileImage
      homepagePreviewImage
    }
  }
`

const GET_LBBS = gql`
  query ($id: ID!, $skip: Int) {
    lBBCategory(id: $id) {
      id
      name
      littleBlackBooks(first: $skip) {
        nodes {
          id
          title
          uri
          featuredImage {
            node {
              sourceUrl
            }
          }
        }
      }
    }
  }
`

interface IBPTLSuggestion {
  isValid: boolean
  emailaddress?: string
  town?: string
}

const Archive = ({
  category,
  parentCategory,
  isBestPlacestoLive,
  countyLatLon,
  postType,
  pagination,
  siteId
}: ArchiveProps) => {
  if (!category) return null
  const countySlug =
    siteId !== 1
      ? `/${
          MULTISITE_COUNTIES.find(c => c.id === siteId)
            ?.url.split('https://')[1]
            .split('.')[0]
        }` ?? '/'
      : ''

  const [adPosition, setAdPosition] = useState<number[]>([0])
  const [view, setView] = useState('grid')
  const { id, databaseId } = category
  const NEEDS_MAP = [
    'cafes',
    'restaurants',
    'pubs',
    'school-reviews',
    'pubs-inns'
  ]
  const bptlState = useContext(BPTLContext)
  const displayMap = category && NEEDS_MAP.includes(category.slug ?? '')
  const queryType = isBestPlacestoLive
    ? GET_BPTL
    : postType?.id === 'lbb'
    ? GET_LBBS
    : GET_POSTS
  const [currentPage, setCurrentPage] = useState<number>(pagination || 1)
  const [currentVisiblePopup, setCurrentVisiblePopup] = useState<
    string | undefined
  >()
  const [suggestion, setSuggestion] = useState<IBPTLSuggestion>({
    isValid: false
  })
  const [suggestionSubmitted, setSuggestionSubmitted] = useState<
    true | false | 'loading'
  >(false)
  const [suggestionError, setSuggestionError] = useState<boolean>(false)
  const [suggestionMessage, setSuggestionMessage] = useState<string>('')
  const [bptlIntroCopy, setBptlIntroCopy] = useState<JSX.Element | undefined>()
  const [orderedPosts, setOrderedPosts] = useState<any>()

  const handleSuggestion = (event: ChangeEvent<HTMLInputElement>) => {
    const initialS = suggestion
    setSuggestion({
      ...initialS,
      [event.target.name]: event.target.value
    })
  }

  useEffect(() => {
    if (bptlState === BPTLStates.REGIONAL) {
      setBptlIntroCopy(
        <div className={ArchiveStyles.BPTLDescription}>
          <p>
            <strong>It’s time to have your say!</strong>
            <br />
            Cast your vote by clicking below on the town or village you think
            deserves to win.
          </p>
        </div>
      )
    } else if (bptlState === BPTLStates.NATIONAL) {
      setBptlIntroCopy(
        <div className={ArchiveStyles.BPTLDescription}>
          <p>
            <strong>THE RESULTS ARE IN!</strong>
            <br />
            Find out where you voted as the hottest place to live in your
            region!
          </p>
        </div>
      )
    } else if (bptlState === BPTLStates.REGIONAL_CLOSED) {
      setBptlIntroCopy(
        <div className={ArchiveStyles.BPTLDescription}>
          <p>
            <strong>REGIONAL VOTING IS CLOSED!</strong>
            <br />
            Winners will be announced on 5 Sept, so watch this space to see
            where you&apos;ve voted as the hottest place to live in your county.
          </p>
        </div>
      )
    }
  }, [bptlState])

  useEffect(() => {
    if (
      !suggestion.isValid &&
      suggestion.hasOwnProperty('emailaddress') &&
      suggestion.emailaddress &&
      suggestion.emailaddress.length > 0 &&
      suggestion.hasOwnProperty('town') &&
      suggestion.town &&
      suggestion.town.length > 0
    ) {
      const initialS = suggestion
      setSuggestion({
        ...initialS,
        isValid: true
      })
    } else if (
      suggestion.isValid &&
      (!suggestion.hasOwnProperty('emailaddress') ||
        !suggestion.emailaddress ||
        suggestion.emailaddress.length < 1 ||
        !suggestion.hasOwnProperty('town') ||
        !suggestion.town ||
        suggestion.town.length < 1)
    ) {
      setSuggestion({
        ...suggestion,
        isValid: false
      })
    }
  }, [suggestion])

  const submitSuggestion = () => {
    if (!suggestion.isValid) return false
    setSuggestionSubmitted('loading')
    bptlVote({
      data: {
        emailaddress: suggestion.emailaddress ?? '',
        county: 'Suggestion',
        town: `${category.name?.replace('*', '')} - ${suggestion.town}`,
        consent: 'no'
      }
    })
      .then(response => {
        if (response.success === false) {
          setSuggestionError(true)
          setSuggestionSubmitted(false)
          setSuggestionMessage(
            'Sorry, there was an error: ' + response.data.message
          )
        } else {
          setSuggestionMessage('Thank you for your suggestion!')
          setSuggestionSubmitted(true)
        }
      })
      .catch(() => {
        setSuggestionError(true)
        setSuggestionSubmitted(false)
        setSuggestionMessage('Sorry, there was an unexplained error')
      })
  }

  useEffect(() => {
    const mob =
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 768px)').matches

    if (mob) {
      setAdPosition([0, 6, 12])
    } else {
      setAdPosition([0, 8, 16])
    }

    // eslint-disable-next-line node/no-unsupported-features/node-builtins
    const currentPage = new URLSearchParams(location.search).get('page')

    if (currentPage) setCurrentPage(parseFloat(currentPage))
  }, [])

  const { data, loading, error } = useQuery(queryType, {
    variables: { id: postType?.id === 'lbb' ? id : databaseId, skip: 200 }
  })

  if (error) return <>Error</>

  const posts = isBestPlacestoLive
    ? data?.bptlPosts
    : postType?.id === 'lbb'
    ? data?.lBBCategory?.littleBlackBooks?.nodes
    : data?.nationalPosts

  const reorderBPTLPosts = () => {
    const county = category.name ?? ''
    if (Object.keys(BPTL_RESULTS).indexOf(county) > -1) {
      const towns = BPTL_RESULTS[county]
      const postcopy = posts
        .slice()
        .sort(
          (a: any, b: any) =>
            towns.indexOf(a.title.split(':')[1].trim()) -
            towns.indexOf(b.title.split(':')[1].trim())
        )

      setOrderedPosts(postcopy)
    }
  }

  useEffect(() => {
    if (posts?.length > 0) {
      if (
        isBestPlacestoLive &&
        (bptlState === BPTLStates.NATIONAL ||
          bptlState === BPTLStates.NATIONAL_CLOSED)
      ) {
        reorderBPTLPosts()
      } else {
        setOrderedPosts(posts)
      }
    }
  }, [posts])

  const postsPerPage = 16
  const pageCount = Math.ceil(posts?.length / postsPerPage)
  const poststoShow = orderedPosts?.slice(
    (currentPage - 1) * postsPerPage,
    currentPage * postsPerPage
  )
  const orphaned = poststoShow ? poststoShow.length % 4 : 0

  const getBreadcrumbRoot = () => {
    if (isBestPlacestoLive)
      return <Link to={`/best-places-to-live`}>Best Places to Live</Link>

    if (postType)
      return <Link to={`/little-black-book`}>Little Black Book</Link>

    return <Link to={`/`}>Home</Link>
  }
  return (
    <>
      {isBestPlacestoLive && (
        <div className={ArchiveStyles.CustomHeader}>
          <h1>Best Places to Live</h1>
          <div>
            <p>In association with</p>
            <div className={ArchiveStyles.SponsorsLogo}>
              <StaticImage
                src={`../../../static/images/best-places-to-live/kf-brandmark-white.png`}
                alt={`Knight Frank`}
                width={200}
              />
            </div>
          </div>
        </div>
      )}
      <div className={ArchiveStyles.Wrapper}>
        <div className={ArchiveStyles.Leader}>
          <div className={BreadcrumbStyles.Base}>
            {getBreadcrumbRoot()}
            {parentCategory && !isBestPlacestoLive && (
              <Link to={`${countySlug}${parentCategory.uri ?? ''}`}>
                {parentCategory.name}
              </Link>
            )}
          </div>
          <h1>{category?.name?.replace('*', '')}</h1>
          {category.slug &&
            category.slug !== 'parenting' &&
            category.slug !== 'home' && (
              <>
                {'sponsoredBy' in category && (
                  <div className={ArchiveStyles.SponsoredBy}>
                    <p>{category.prefix}</p>
                    {category.sponsoredLogo && (
                      <img src={category.sponsoredLogo} />
                    )}
                  </div>
                )}
              </>
            )}

          {
            <>
              {category.slug && category.slug === 'parenting' && (
                <div className={ArchiveStyles.SponsoredBy}>
                  <p>In association with</p>

                  <a
                    href={`https://www.startriteshoes.com?utm_campaign=schoolcampaign&utm_medium=referral&utm_source=muddystilettoes`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <StaticImage
                      width={300}
                      className={ArchiveStyles.MadeImage}
                      src={`../../../static/images/sponsors/sr-black.png`}
                      alt={`Start-Rite`}
                    />
                  </a>
                </div>
              )}
            </>
          }

          {
            <>
              {category.slug && category.slug === 'home' && (
                <div className={ArchiveStyles.MadeSponsor}>
                  <p>In association with</p>

                  <StaticImage
                    width={250}
                    className={ArchiveStyles.MadeImage}
                    // height={100}
                    src={`../../../static/images/sponsors/made.png`}
                    alt={`MADE.COM`}
                  />
                </div>
              )}
            </>
          }
          {category.description && (
            <p dangerouslySetInnerHTML={{ __html: category.description }} />
          )}
          {isBestPlacestoLive && bptlIntroCopy}
        </div>
        {category.children?.nodes && category.children.nodes.length > 0 && (
          <Subnav>
            <ul>
              <li>
                <a href="" data-id="first">
                  All
                </a>
              </li>
              {category.children.nodes &&
                category.children.nodes.map(
                  (child, index) =>
                    child && (
                      <li key={index}>
                        <Link
                          to={
                            siteId !== 1
                              ? `${countySlug}${child.uri ?? ''}`
                              : child.uri ?? ''
                          }
                        >
                          {child.name}
                        </Link>
                      </li>
                    )
                )}
            </ul>
          </Subnav>
        )}
        {isBestPlacestoLive && bptlState !== BPTLStates.NATIONAL && (
          <Subnav alignCenter>
            <ul>
              <li
                onClick={() => setView('grid')}
                data-id={`${view === 'grid' ? 'isActive' : ''}`}
              >
                Grid view
              </li>
              <li
                onClick={() => setView('list')}
                data-id={`${view === 'list' ? 'isActive' : ''}`}
              >
                Map view
              </li>
            </ul>
          </Subnav>
        )}
        <Hr />
        {displayMap && countyLatLon && category.databaseId && category.slug && (
          <Map
            countyLatLon={countyLatLon}
            databaseId={category.databaseId}
            category={category.slug}
            zoom={11}
          />
        )}
        {isBestPlacestoLive &&
        (bptlState === BPTLStates.NATIONAL ||
          bptlState === BPTLStates.NATIONAL_CLOSED) ? (
          <>
            <Blocks
              posts={poststoShow}
              hasSpotlight={true}
              isBptl={true}
              settings={{
                spotlightText: 'bptlresults'
              }}
            />
          </>
        ) : (
          <>
            {view === 'grid' && (
              <div
                className={`${ArchiveStyles.Blocks} ${
                  orphaned === 2 ? ArchiveStyles.DoubleMargin : ''
                } ${orphaned === 3 ? ArchiveStyles.SingleMargin : ''}`}
              >
                {loading && <SkeletonBlocks blocksNumber={12} order={0} />}
                {poststoShow &&
                  poststoShow
                    .slice(0, adPosition[1])
                    .map((post: any) => (
                      <Fragment key={post.id}>
                        {isBestPlacestoLive && category.name ? (
                          <BPTLBlock
                            post={post}
                            setPopup={setCurrentVisiblePopup}
                            isVisible={currentVisiblePopup == post.id}
                            siteId={siteId}
                            catName={category.name}
                          />
                        ) : (
                          <ArchiveBlock key={post.id} post={post} />
                        )}
                      </Fragment>
                    ))}

                {!isBestPlacestoLive && (
                  <div style={{ marginBottom: '20px', width: '100%' }}>
                    <MPUBanner
                      adSlots={['Row1MPU1', 'Row1MPU2', 'Row1MPU3', 'Row1MPU4']}
                    />
                  </div>
                )}

                {poststoShow &&
                  poststoShow
                    .slice(adPosition[1], adPosition[2])
                    .map((post: any) => (
                      <Fragment key={post.id}>
                        {isBestPlacestoLive && category.name ? (
                          <BPTLBlock
                            post={post}
                            setPopup={setCurrentVisiblePopup}
                            isVisible={currentVisiblePopup == post.id}
                            siteId={siteId}
                            catName={category.name}
                          />
                        ) : (
                          <ArchiveBlock key={post.id} post={post} />
                        )}
                      </Fragment>
                    ))}

                {!isBestPlacestoLive && (
                  <div style={{ marginBottom: '20px', width: '100%' }}>
                    <MPUBanner
                      adSlots={['Row2MPU1', 'Row2MPU2', 'Row2MPU3', 'Row2MPU4']}
                    />
                  </div>
                )}

                {poststoShow &&
                  poststoShow
                    .slice(adPosition[2], poststoShow.length)
                    .map((post: any) => (
                      <Fragment key={post.id}>
                        {isBestPlacestoLive && category.name ? (
                          <BPTLBlock
                            post={post}
                            setPopup={setCurrentVisiblePopup}
                            isVisible={currentVisiblePopup == post.id}
                            siteId={siteId}
                            catName={category.name}
                          />
                        ) : (
                          <ArchiveBlock key={post.id} post={post} />
                        )}
                      </Fragment>
                    ))}
              </div>
            )}
            {!isBestPlacestoLive && (
              <div className={ArchiveStyles.Pagination}>
                <div>
                  {currentPage > 1 ? (
                    <Link
                      to={`${
                        currentPage > 2 ? `?page=${currentPage - 1}` : ``
                      }`}
                      onClick={() => setCurrentPage(currentPage - 1)}
                    >
                      Previous
                    </Link>
                  ) : (
                    <>Previous</>
                  )}
                </div>
                <div className={ArchiveStyles.PageCount}>
                  {Array.from({ length: pageCount }).map((_, index) => (
                    <Link
                      to={`${index > 0 ? `?page=${index + 1}` : ''}`}
                      key={index}
                      className={classNames({
                        [ArchiveStyles.Active]: currentPage === index + 1,
                        [ArchiveStyles.AfterActive]: currentPage === index,
                        [ArchiveStyles.BeforeActive]:
                          index !== 0 && currentPage === index + 2,
                        [ArchiveStyles.Hidden]:
                          index !== 0 &&
                          index !== pageCount - 1 &&
                          index !== currentPage &&
                          index !== currentPage - 2 &&
                          index !== currentPage - 1
                      })}
                      onClick={() => setCurrentPage(index + 1)}
                    >
                      {index + 1}
                    </Link>
                  ))}
                </div>
                <div>
                  {currentPage < pageCount ? (
                    <Link
                      to={`?page=${currentPage + 1}`}
                      onClick={() => setCurrentPage(currentPage + 1)}
                    >
                      Next
                    </Link>
                  ) : (
                    <>Next</>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {view === 'list' && (
        <div className={BPTLStyles.CountyList}>
          <div className={BPTLStyles.CountyMap}>
            {countyLatLon && category.databaseId && category.slug && (
              <Map
                countyLatLon={countyLatLon}
                databaseId={category.databaseId}
                category={category.slug}
                zoom={9}
              />
            )}
          </div>
          <ul>
            {poststoShow.map((post: any) => (
              <li key={post.id}>
                <Link to={post.uri}>
                  {post.title.replace('Best Places to Live:', '')}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isBestPlacestoLive && (
        <>
          <div>
            <h3 className={ArchiveStyles.Leader}>
              View the full{' '}
              <a href={`https://muddystilettos.co.uk/best-places-to-live/`}>
                Top 260 Places to Live
              </a>
            </h3>
          </div>
          <div className={ArchiveStyles.SuggestYourOwn}>
            <div className={ArchiveStyles.SuggestWrapper}>
              <h2>
                Know another brilliant place to live in{' '}
                {category.name?.replace('*', '')}?
              </h2>
              <p>
                Tell us your favourite town or village and we'll check it out
                for our next edition of Best Places to Live.
              </p>
              {suggestionSubmitted === true ? (
                <p>{suggestionMessage}</p>
              ) : (
                <div className={ArchiveStyles.SuggestForm}>
                  <div className={ArchiveStyles.InputWrapper}>
                    <input
                      type="text"
                      name="emailaddress"
                      defaultValue={''}
                      placeholder="Your email address"
                      onChange={e => handleSuggestion(e)}
                    />
                    <input
                      type="text"
                      name="town"
                      defaultValue=""
                      placeholder="Your suggestion"
                      onChange={e => handleSuggestion(e)}
                    />
                    <button
                      disabled={
                        !suggestion.isValid || suggestionSubmitted === 'loading'
                      }
                      onClick={submitSuggestion}
                      className={ArchiveStyles.Button}
                    >
                      {suggestionSubmitted === 'loading'
                        ? 'Submitting...'
                        : 'Send Suggestion'}
                    </button>
                  </div>
                  {suggestionError && <p>{suggestionMessage}</p>}
                </div>
              )}
            </div>
          </div>
        </>
      )}
      <Hr />
    </>
  )
}

export default Archive
